import http from 'axios';
import { trackAction } from 'helpers/trackingOM';

const linkedPromptMapping = {
    decline: 'linkedPromptDecline',
    dismiss: 'linkedPromptDismiss',
    timeout: 'linkedPromptTimeout',
    accept: 'linkedPromptAccept',
    secondaryAccept: 'linkedPromptSecondaryAccept',
};

export default class RedfastService {
    constructor({ apiDomain, appId, locale = '', subscriptionCountry = '', userId }) {
        this.appId = appId;
        this.deviceType = 'web-api';
        this.endpoint = apiDomain;
        this.eventListeners = {};
        this.locale = locale;
        this.locale = locale;
        this.openedPrompts = {};
        this.prompts = [];
        this.segments = [];
        this.subscriptionCountry = subscriptionCountry;
        this.userId = userId;
        this.getUserPrompts();
    }

    getTrackingParams(prompt, trigger) {
        return {
            pageUrl: window.location.href,
            pageType: CBS.Tracking.utag_data.pageType,
            modalId: prompt.id,
            modalName: prompt.name,
            modalSourceId: trigger.id,
            modalSource: trigger.click_id,
            modalVariantId: prompt.action_group_id,
            modalVariantName: prompt.action_group_name || 'na',
            modalMessage: `${prompt.actions.rf_retention_title} ${prompt.actions.rf_retention_message}}`,
        };
    }

    getUserPrompts() {
        // the /ping endpoint needs to be called 2 times in order for Prompt data to be returned from Redfast as per their docs
        // https://docs.redfast.com/advanced/platforms/promo-custom-render.html#sequence-diagram
        return this.ping()
            .then((result) => {
                return result
                    ? new Promise((resolve, reject) => {
                          setTimeout(() => {
                              return this.ping().then(({ data }) => resolve(data));
                          }, result?.data?.configs?.ping_frequency || 500);
                      })
                    : undefined; // no data from redfast (ie for anonymous users)
            })
            .then((data) => {
                if (!data) {
                    return;
                }

                this.prompts = data.paths;
                this.segments = data.segments;
                this.attachEventListeners();
                this.listenForUrlTriggers();
            });
    }

    ping() {
        const headers = this.userId
            ? {
                  'user-id': this.userId,
              }
            : {
                  'anonymous-user-id': 'anonymous-user-id',
              };
        const params = {
            device_type: this.deviceType,
            language: this.subscriptionCountry,
        };

        return http
            .get(`${this.endpoint}/ping/${this.appId}`, { headers, params })
            .catch(this.trackRedfastError);
    }

    listenForUrlTriggers() {
        // we want to check on first load
        this.checkUrlPathForMatchingPrompts(window.location.pathname);

        // and then on any subsequent url changes
        window.addEventListener('locationchange', (event) => {
            this.checkUrlPathForMatchingPrompts(window.location.pathname);
        });
    }

    checkUrlPathForMatchingPrompts(path) {
        for (const prompt of this.prompts) {
            for (const trigger of prompt.triggers) {
                // if we have a click_id then it will be handled when we attach click listeners and we want to skip this trigger
                if (trigger.click_id) {
                    continue;
                }

                if (trigger.url_path === '*' || this.matchesUrlPath(trigger, path)) {
                    this.openPrompt(prompt, trigger);
                    // once a URL trigger is found, we dont care about matching any further triggers for the prompt
                    break;
                }
            }
        }
    }

    matchesUrlPath(trigger, currentUrl) {
        if (!trigger.url_path) {
            return false;
        }

        let matchesUrl = false;
        if (trigger.url_path === '*') {
            matchesUrl = true;
        } else {
            const useRegex = trigger.use_regex;
            const urlExp = useRegex ? RegExp(`${trigger.url_path}`, 'g') : null;
            matchesUrl = useRegex
                ? urlExp.test(currentUrl)
                : currentUrl?.includes(trigger.url_path);
        }

        return matchesUrl;
    }

    /* 
        Takes a string and turns it into a Event Handler Config object
        returns Object { eventType (String), selector (String)}
    */
    getEventHandlerConfig(trigger) {
        let config = {
            eventType: 'click',
            selector: trigger,
        };
        const delimiter = ':';

        // if ':' delimeter is present split to set CustomEvents
        if (trigger.indexOf(delimiter) > -1) {
            let config_array = trigger.split(delimiter); // [EventType, selector]
            if (trigger.split.length === 2) {
                config.eventType = config_array[0];
                config.selector = config_array[1];
            } else {
                console.error('Invalid click_id for prompt');
            }
        }
        return config;
    }

    attachEventListeners() {
        this.cleanupEventListeners();

        for (const prompt of this.prompts) {
            for (const trigger of prompt.triggers) {
                // if no element to attach our trigger, continue to next trigger
                if (!trigger.click_id) {
                    continue;
                }

                const hasURLMatcher = !!trigger.url_path;

                if (hasURLMatcher) {
                    const matchesUrl = this.matchesUrlPath(trigger, window.location.pathname);

                    if (!matchesUrl) {
                        continue;
                    }
                }

                const eventHandlerConfig = this.getEventHandlerConfig(trigger.click_id);

                const triggerNodes = Array.from(
                    document.querySelectorAll(eventHandlerConfig.selector),
                );
                const eventType = eventHandlerConfig.eventType;

                for (const triggerNode of triggerNodes) {
                    const listener = (event) => {
                        this.openPrompt(prompt, trigger);
                    };

                    /* handle special exists case 
                        if eventType is exists we check to see if that selector is present and open the prompt
                    */
                    if (eventType === 'exists') {
                        this.openPrompt(prompt, trigger);
                        continue;
                    } else {
                        triggerNode.addEventListener(eventType, listener);

                        // to our eventListeners object so we can remove them later
                        this.eventListeners[trigger.click_id] = {
                            node: triggerNode,
                            listener,
                            eventType,
                        };
                    }
                }
            }
        }
    }

    cleanupEventListeners() {
        for (const [key, { node, listener, eventType }] of Object.entries(this.eventListeners)) {
            node.removeEventListener(eventType, listener);
        }

        this.eventListeners = {};
    }

    openPrompt(prompt, trigger) {
        // placed in setTimeout because our trackModalView seems to take over the initial trackState network call and replace it with our trackModalView info
        // which makes 2 network calls for trackModalView instead of one (and erasing the initial trackState call)
        // https://paramountplus.atlassian.net/browse/PPIWEBM-795
        setTimeout(() => {
            trackAction('trackModalView', {
                eventModalView: 1,
                ...this.getTrackingParams(prompt, trigger),
            });
        });

        if (this.openedPrompts[prompt.id]) {
            return;
        }
        this.openedPrompts[prompt.id] = true;

        const dialog = this.createDialog(prompt, trigger);
        dialog.showModal();
        document.body.style.overflow = 'hidden';
        this.sendImpression(prompt)
            .then()
            .catch((error) => {
                // TODO handle error
            });
    }

    createDialog(prompt, trigger) {
        // config attributes -> https://docs.redfast.com/advanced/platforms/promo-types-actions.html#attributes-by-device
        const dialog = document.createElement('dialog');
        dialog.className = 'rfdialog';
        dialog.id = `rfdialog-${prompt.id}`;

        const wrapper = document.createElement('div');
        wrapper.className = 'rfdialog__wrapper';

        if (prompt.actions.rf_settings_close_button_enabled) {
            const closeButton = this.createCloseButton(prompt, trigger);
            wrapper.appendChild(closeButton);
        }

        const backgroundImage = this.createBackgroundImage();
        wrapper.appendChild(backgroundImage);
        const content = this.createContent(prompt, trigger);
        wrapper.appendChild(content);
        dialog.appendChild(wrapper);

        const overlay =
            document.getElementById('rfdialog__overlay') ||
            this.createOverlay(dialog, prompt, trigger);
        overlay.appendChild(dialog);
        document.body.appendChild(overlay);
        // create a stylesheet and add to the <head> of document
        this.createDialogStyles(prompt);

        return dialog;
    }

    createOverlay(dialog, prompt, trigger) {
        const config = prompt.actions;
        const overlay = document.createElement('div');
        overlay.className = 'rfdialog__overlay';
        overlay.id = 'rfdialog__overlay';

        overlay.onclick = (event) => {
            // whether or not we allow closing the dialog by clicking the overlay
            if (!config.rf_settings_click_outside_close_enabled) {
                return;
            }

            const rect = dialog.getBoundingClientRect();
            const isInDialog =
                rect.top <= event.clientY &&
                event.clientY <= rect.top + rect.height &&
                rect.left <= event.clientX &&
                event.clientX <= rect.left + rect.width;

            if (!isInDialog) {
                this.onDismiss(prompt, trigger, 'dismiss');
            }
        };

        return overlay;
    }

    createCloseButton(prompt, trigger) {
        const closeButton = document.createElement('span');
        closeButton.className = 'rfdialog__close';
        const closeIcon = document.createElement('i');
        closeIcon.className = 'rfdialog__close-icon';
        closeIcon.innerHTML = '&#x2715;';
        closeButton.appendChild(closeIcon);
        closeButton.onclick = (event) => {
            trackAction('trackModalDismiss', {
                eventModalDismiss: 1,
                ...this.getTrackingParams(prompt, trigger),
            });
            this.onDismiss(prompt, trigger, 'dismiss');
            event.stopPropagation();
        };

        return closeButton;
    }

    createBackgroundImage() {
        const backgroundImage = document.createElement('div');
        backgroundImage.className = 'rfdialog__background';

        return backgroundImage;
    }

    createContent(prompt, trigger) {
        const content = document.createElement('div');
        content.className = 'rfdialog__content';

        const textSection = this.createTextSection(prompt, trigger);
        const actions = this.createActions(prompt, trigger);
        const countdown = this.createCountdown(prompt, trigger);
        const legal = this.createLegal(prompt, trigger);
        const spinner = this.createSpinner(prompt);

        content.appendChild(textSection);
        content.appendChild(actions);
        content.appendChild(countdown);
        content.appendChild(legal);
        content.appendChild(spinner);

        return content;
    }

    createSpinner(prompt) {
        const wrapper = document.createElement('div');
        wrapper.id = `prompt-spinner-${prompt.id}`;
        wrapper.className = 'spinner-wrapper';
        const spinner = document.createElement('div');
        spinner.className = 'spinner-area';

        const spinnerChild = document.createElement('div');
        spinnerChild.className = 'spinner';

        for (let i = 0; i < 13; i++) {
            const child = document.createElement('div');
            spinnerChild.appendChild(child);
        }

        spinner.appendChild(spinnerChild);
        wrapper.appendChild(spinner);
        wrapper.style.display = 'none';
        wrapper.style.justifyContent = 'center';

        return wrapper;
    }

    createTextSection(prompt, trigger) {
        const texts = document.createElement('div');
        texts.className = 'rfdialog__texts';

        const title = document.createElement('h1');
        title.className = 'rfdialog__texts-header';
        title.setAttribute('text', prompt.actions.rf_retention_title);
        title.setAttribute('text-mobile', prompt.actions.rf_mobile_title);
        texts.appendChild(title);

        const message = document.createElement('p');
        message.className = 'rfdialog__texts-message';
        message.setAttribute('text', prompt.actions.rf_retention_message);
        message.setAttribute('text-mobile', prompt.actions.rf_mobile_message);
        texts.appendChild(message);

        return texts;
    }

    createActions(prompt, trigger) {
        const actions = document.createElement('div');
        actions.className = 'rfdialog__actions';

        if (prompt.actions.rf_retention_confirm_button_text) {
            const acceptAction = document.createElement('button');
            acceptAction.className =
                'button rfdialog__actions-button rfdialog__actions-button-primary';
            acceptAction.setAttribute('data-ci', 'primaryAcceptButton');
            const buttonText = document.createElement('div');
            buttonText.className = 'button__text';
            buttonText.textContent = prompt.actions?.rf_retention_confirm_button_text;
            acceptAction.appendChild(buttonText);
            actions.appendChild(acceptAction);
            acceptAction.onclick = (event) => {
                this.onPrimaryAccept(prompt, trigger);
                event.stopPropagation();
            };
        }

        if (prompt.actions.rf_settings_confirm_button_2_enabled) {
            const acceptAction2 = document.createElement('button');
            acceptAction2.className =
                'button rfdialog__actions-button rfdialog__actions-button-secondary';
            const buttonText = document.createElement('div');
            buttonText.className = 'button__text';
            buttonText.textContent = prompt.actions.rf_retention_confirm_button_2_text;
            acceptAction2.appendChild(buttonText);
            actions.appendChild(acceptAction2);
            acceptAction2.onclick = (event) => {
                this.onSecondaryAccept(prompt, trigger);
                event.stopPropagation();
            };
        }

        if (prompt.actions.rf_settings_cancel_button_enabled) {
            const cancelAction = document.createElement('button');
            cancelAction.className =
                'button secondary rfdialog__actions-button rfdialog__actions-button-secondary';
            const buttonText = document.createElement('div');
            buttonText.className = 'button__text';
            buttonText.textContent = prompt.actions.rf_retention_cancel_button_text;
            cancelAction.appendChild(buttonText);
            cancelAction.onclick = (event) => {
                trackAction('trackModalDecline', {
                    eventModalDecline: 1,
                    ctaText: prompt.actions.rf_retention_cancel_button_text,

                    ...this.getTrackingParams(prompt, trigger),
                });
                this.onDismiss(prompt, trigger, 'decline');
                event.stopPropagation();
            };
            actions.appendChild(cancelAction);
        }

        return actions;
    }

    createCountdown(prompt, trigger) {
        // if we don't have a timer, just return empty div
        if (!prompt.actions.rf_settings_close_seconds) {
            return document.createElement('div');
        }

        const countdown = document.createElement('div');
        countdown.className = 'rfdialog__countdown';

        let secondsRemaining = parseInt(prompt.actions.rf_settings_close_seconds);
        countdown.textContent = `${secondsRemaining} ${prompt.actions.rf_settings_close_seconds_text}`;

        const downloadTimer = setInterval(() => {
            if (secondsRemaining <= 0) {
                clearInterval(downloadTimer);
                trackAction('trackModalTimeout', {
                    eventModalTimeout: 1,
                    ...this.getTrackingParams(prompt, trigger),
                });
                this.onDismiss(prompt, trigger, 'timeout');
            } else {
                secondsRemaining -= 1;
                countdown.textContent = `${secondsRemaining} ${prompt.actions.rf_settings_close_seconds_text}`;
            }
        }, 1000);

        return countdown;
    }

    createLegal(prompt, trigger) {
        const legal = document.createElement('div');
        const legalType =
            prompt.actions.rf_settings_privacy_policy_type === 'long' ? 'long' : 'short';
        legal.className = 'rfdialog__legal';

        switch (legalType) {
            case 'long':
                if (!prompt.actions.rf_settings_privacy_policy_text) {
                    return legal;
                }

                legal.classList.add('rfdialog__legal-long');
                legal.textContent = prompt.actions.rf_settings_privacy_policy_text;

                break;
            case 'short':
                if (
                    !prompt.actions.rf_settings_privacy_policy_link &&
                    !prompt.actions.rf_settings_tos_link
                ) {
                    return legal;
                }

                // TODO: Create POEditor term to support this in the future
                // Can have parameters for prompt.actions.rf_settings_privacy_policy_link and prompt.actions.rf_settings_tos_link
                legal.innerHTML = '';

                break;
        }

        return legal;
    }

    createDialogStyles(prompt, trigger) {
        const mobileStyles = this.getMobileStyles(prompt, trigger);
        const desktopStyles = this.getDesktopStyles(prompt, trigger);
        const largeScreenStyles = this.getLargeScreenStyles(prompt, trigger);
        // TODO is this what we want our mobile breakpoint to be set as?
        const mobileBreakpoint = 1023;
        const desktopBreakpoint = 1920;

        const css = `
            ${mobileStyles}

            @media only screen and (min-width : ${mobileBreakpoint}px) {
                ${desktopStyles}
            }

            @media only screen and (min-width : ${desktopBreakpoint}px) {
                ${largeScreenStyles}
            }
        `;

        const style = document.createElement('style');
        style.setAttribute('id', prompt.id);
        style.appendChild(document.createTextNode(css));
        const head = document.getElementsByTagName('head')[0];
        head.appendChild(style);
    }

    getMobileStyles(prompt, trigger) {
        return `
            .rfdialog__overlay {
                background-color: ${prompt.actions.rf_settings_overlay_background};
                width: 100%;
                height: 100%;
                position: fixed;
                z-index: 1000001;
                display: flex;
                justify-content: center;
                align-items: center;
                height: 100vh;
                top: 0;
            }

            #rfdialog-${prompt.id}.rfdialog {
                padding: 0;
            }
            
            body.round-corners #rfdialog-${prompt.id}.rfdialog{
                border-radius: 6px;
                /* mobile landscape */
                @media (min-width: 480px){
                    border-radius: 8px;
                }
                /* tablet portrait */
                @media (min-width: 768px){
                    border-radius: 10px;
                }
            }

            #rfdialog-${prompt.id} .rfdialog__wrapper {
                display: flex;
                flex-direction: column;
                justify-content: flex-end;
                width: 330px;
                min-height: 626px;
                position: relative;
            }

            #rfdialog-${prompt.id} .rfdialog__close {
                z-index: 1;
                font-size: 28px;
                cursor: pointer;
                position: absolute;
                right: 20px;
                top: 20px;
                line-height: 10px;
            }

            #rfdialog-${prompt.id} .rfdialog__close-icon {
                color: white;
                font-style: normal;
            }

            #rfdialog-${prompt.id} .rfdialog__background {
                width: 100%;
                height: 185px;

                background-size: cover;
                background-repeat: no-repeat;
                background-position: top;
                background-image: ${
                    prompt.actions.rf_settings_mobile_bg_image
                        ? `url(${prompt.actions.rf_settings_mobile_bg_image})`
                        : 'none'
                };
                background-color: ${prompt.actions.rf_settings_bg_image_color};
            }

            #rfdialog-${prompt.id} .rfdialog__content {
                display: flex;
                flex-direction: column;
                padding: 24px 20px 20px 20px;
                flex: 1;
            }

            #rfdialog-${prompt.id} .rfdialog__texts {
                display: flex;
                flex-direction: column;
            }

            #rfdialog-${prompt.id} .rfdialog__texts-header {
                margin: 0;
                font-size: 32px;
                color: ${prompt.actions.rf_settings_fill_color || 'white'};
            }

            #rfdialog-${prompt.id} .rfdialog__texts-header::before {
                content: attr(text-mobile);
                white-space: break-spaces;
            }

            #rfdialog-${prompt.id} .rfdialog__texts-message {
                font-size: 18px;
                color: ${prompt.actions.rf_settings_fill_color || 'white'};
                padding: 10px 0 10px 0;
                margin: 0;
            }

            #rfdialog-${prompt.id} .rfdialog__texts-message::before {
                content: attr(text-mobile);
                white-space: break-spaces;
            }

            #rfdialog-${prompt.id} .rfdialog__actions {
                display: flex;
                flex-direction: column;
                margin-top: auto;
            }

            #rfdialog-${prompt.id} .rfdialog__actions .rfdialog__actions-button:not(:first-child) {
                margin-top: 20px;
             }

            #rfdialog-${prompt.id} .rfdialog__actions-button {
                font-size: 15px;
                padding: 12px 20px 12px 20px;
            }

            #rfdialog-${prompt.id} .rfdialog__actions-button-primary {
            }

            #rfdialog-${prompt.id} .rfdialog__actions-button-secondary {
                color: #0037c5;
                margin-top: 10px;
            }

            #rfdialog-${prompt.id} .rfdialog__countdown {
                opacity: .7;
                text-align: center;
                padding: 10px 0 10px;
            }

            #rfdialog-${prompt.id} .rfdialog__legal {
                padding-top: 16px;
                font-size: 10px;
                line-height: 14px;
            }

            #rfdialog-${prompt.id} .rfdialog__legal-long {

            }
        `;
    }

    getDesktopStyles(prompt, trigger) {
        const hasLegalText = !!prompt.actions.rf_settings_privacy_policy_text;

        return `
            .rfdialog__overlay {
            }

            #rfdialog-${prompt.id}.rfdialog {
            }

            #rfdialog-${prompt.id} .rfdialog__wrapper {
                width: fit-content;
                flex-direction: row;
                min-height: 580px;
            }

            #rfdialog-${prompt.id} .rfdialog__close {
            }

            #rfdialog-${prompt.id} .rfdialog__close-icon {
                color: black;
            }

            #rfdialog-${prompt.id} .rfdialog__background {
                background-position: top;
                background-image: ${
                    prompt.actions.rf_settings_bg_image
                        ? `url(${prompt.actions.rf_settings_bg_image})`
                        : 'none'
                };
                background-color: ${prompt.actions.rf_settings_bg_image_color};
                height: auto;
                width: 388px;
            }

            #rfdialog-${prompt.id} .rfdialog__content {
                width: 324px;
                padding: 56px 32px 32px 32px;
            }

            #rfdialog-${prompt.id} .rfdialog__texts {
                height: 100%;
            }

            #rfdialog-${prompt.id} .rfdialog__texts-header {
                font-size: 40px;
                font-weight: 900;
                line-height: 40px;
            }

            #rfdialog-${prompt.id} .rfdialog__texts-header::before {
                content: attr(text);
                white-space: break-spaces;
            }

            #rfdialog-${prompt.id} .rfdialog__texts-message {
                font-size: 16px;
                font-weight: 400;
                line-height: 22px;
            }

            #rfdialog-${prompt.id} .rfdialog__texts-message::before {
                content: attr(text);
                white-space: break-spaces;
            }

            #rfdialog-${prompt.id} .rfdialog__actions {
                flex-direction: ${hasLegalText ? 'row' : 'column'};
                align-items: ${hasLegalText ? 'center' : 'initial'};
            }

            #rfdialog-${prompt.id} .rfdialog__actions .rfdialog__actions-button:not(:first-child) {
                margin-top: ${hasLegalText ? '0' : '10px'};
            }

            #rfdialog-${prompt.id} .rfdialog__actions-button {
                font-size: 12px;
                width: 50%;
            }

            #rfdialog-${prompt.id} .rfdialog__actions-button-primary {
                margin-right: ${hasLegalText ? '16px' : '0'};
            }

            #rfdialog-${prompt.id} .rfdialog__actions-button-secondary { 
            }

            #rfdialog-${prompt.id} .rfdialog__countdown {
            }

            #rfdialog-${prompt.id} .rfdialog__legal {
            }

            #rfdialog-${prompt.id} .rfdialog__legal-long {

            }
        `;
    }

    getLargeScreenStyles(prompt, trigger) {
        return `
            #rfdialog-${prompt.id}.rfdialog {
            }

            #rfdialog-${prompt.id} .rfdialog__wrapper {
                min-height: 842px;
            }

            #rfdialog-${prompt.id} .rfdialog__content {
                width: 562px;
                padding: 76px 48px 48px 48px;
            }

            #rfdialog-${prompt.id} .rfdialog__background {
                width: 562px;
            }

            #rfdialog-${prompt.id} .rfdialog__texts-header {
                font-size: 60px;
                line-height: 60px;
            }

            #rfdialog-${prompt.id} .rfdialog__texts-message {
                font-size: 28px;
                line-height: 36px;
            }

            #rfdialog-${prompt.id} .rfdialog__actions {
                flex-direction: row;
            }

            #rfdialog-${prompt.id} .rfdialog__actions .rfdialog__actions-button:first-child {
                margin-right: 15px;
            }

            #rfdialog-${prompt.id} .rfdialog__actions .rfdialog__actions-button:not(:first-child) {
                margin-top: 0;
            }

            #rfdialog-${prompt.id} .rfdialog__actions-button {
                font-size: 15px;
                width: 221px;
            }

            #rfdialog-${prompt.id} .rfdialog__legal {
                padding-top: 24px;
                font-size: 14px;
                line-height: 18px;
            }
        `;
    }

    onDismiss(prompt, trigger, dismissType) {
        const linkedPromptType = linkedPromptMapping[dismissType];
        const linkedPromptId = prompt.actions?.rf_metadata
            ? prompt.actions?.rf_metadata[linkedPromptType]
            : null;

        if (linkedPromptId) {
            this.openLinkedPrompt(linkedPromptId, trigger, 'decline');
        }

        this.closeDialog(prompt.id);
        this.sendDismiss(prompt, dismissType)
            .then()
            .catch((error) => {
                // TODO handle error
            });
    }

    isCallbackBasedPrompt({ callbackSuccessPrompt, callbackFailPrompt, callbackUrl }) {
        return callbackSuccessPrompt && callbackFailPrompt && callbackUrl;
    }

    hideButtonsAndShowLoader(promptId) {
        const buttonContainer = document.querySelector('div.rfdialog__actions');
        const spinner = document.querySelector(`#prompt-spinner-${promptId}`);
        if (!buttonContainer || !spinner) {
            return;
        }
        buttonContainer.style.display = 'none';
        spinner.style.display = 'flex';
    }

    async getResponseAndOpenPrompt(
        { callbackSuccessPrompt, callbackFailPrompt, callbackUrl },
        promptId,
        trigger,
    ) {
        this.hideButtonsAndShowLoader(promptId);
        let response;
        try {
            response = await http.get(callbackUrl);
        } catch (e) {
            this.closeDialog(promptId);
            this.openLinkedPrompt(callbackFailPrompt, trigger, 'accept');
            return;
        }
        this.closeDialog(promptId);

        if (response.data.success) {
            this.openLinkedPrompt(callbackSuccessPrompt, trigger, 'accept');
        } else {
            this.openLinkedPrompt(callbackFailPrompt, trigger, 'accept');
        }
    }

    async onPrimaryAccept(prompt, trigger) {
        const callbackProps = {
            callbackSuccessPrompt: prompt.actions.rf_metadata?.callbackSuccessPrompt,
            callbackFailPrompt: prompt.actions.rf_metadata?.callbackFailPrompt,
            callbackUrl: prompt.actions.rf_metadata?.callbackUrl,
        };
        if (this.isCallbackBasedPrompt(callbackProps)) {
            await this.getResponseAndOpenPrompt(callbackProps, prompt.id, trigger);
            return;
        }
        const navigateRoute = prompt.actions.rf_metadata?.accept;
        const promptId = prompt.id;

        if (navigateRoute) {
            this.goToRoute(navigateRoute);
        }

        this.closeDialog(promptId);
        trackAction('trackModalGoal', {
            eventModalGoal: 1,
            ctaText: prompt.actions.rf_retention_confirm_button_text,
            ...this.getTrackingParams(prompt, trigger),
        });
        const linkedPromptId = prompt.actions?.rf_metadata?.linkedPromptAccept;

        if (linkedPromptId) {
            this.openLinkedPrompt(linkedPromptId, trigger, 'goal');
        }

        this.sendGoal(prompt)
            .then()
            .catch((error) => {
                // TODO handle error
            });
    }

    async onSecondaryAccept(prompt, trigger) {
        const callbackProps = {
            callbackSuccessPrompt: prompt.actions.rf_metadata?.secondaryCallbackSuccessPrompt,
            callbackFailPrompt: prompt.actions.rf_metadata?.secondaryCallbackFailPrompt,
            callbackUrl: prompt.actions.rf_metadata?.secondaryCallbackUrl,
        };
        if (this.isCallbackBasedPrompt(callbackProps)) {
            await this.getResponseAndOpenPrompt(callbackProps, prompt.id, trigger);
            return;
        }
        const navigateRoute = prompt.actions.rg_metadata.secondaryAccept;

        if (navigateRoute) {
            this.goToRoute(navigateRoute);
        }

        this.closeDialog(prompt.id);
        const linkedPromptId = prompt.actions?.rf_metadata?.linkedPromptSecondaryAccept;

        if (linkedPromptId) {
            this.openLinkedPrompt(linkedPromptId, trigger, 'goal');
        }
    }

    goToRoute(route) {
        window.location.href = route;
    }

    openLinkedPrompt(promptId, trigger, triggerType) {
        // in order for stacked prompts to work, we grab the promptId from prompt.actions.rf_metadata
        // this is editable inside the prompt in Redfast.
        // here are the keys to use that correspond to the action the user clicks
        // const device_metadata = {
        //    linkedPromptAccept: promptId,
        //    linkedPromptSecondaryAccept: promptId,
        //    linkedPromptDismiss: promptId,
        //    linkedPromptDecline: promptId,
        //    linkedPromptTimeout: promptId
        // };
        const prompt = this.prompts.find((prompt) => prompt.id === promptId);
        trigger.click_id = `${trigger.click_id} - ${triggerType}`;

        if (prompt) {
            this.openPrompt(prompt, trigger);
        }
    }

    closeDialog(pathId) {
        delete this.openedPrompts[pathId];
        const dialog = document.getElementById(`rfdialog-${pathId}`);
        dialog?.close();
        dialog?.remove();
        document.body.style.overflow = 'initial';

        const openedDialops = Object.keys(this.openedPrompts).length;

        if (openedDialops === 0) {
            const overlay = document.getElementById('rfdialog__overlay');
            overlay?.remove();
        }
    }

    // https://docs.redfast.com/advanced/platforms/promo-custom-render.html#dismiss-api
    sendImpression(prompt) {
        const headers = {
            'user-id': this.userId,
        };

        const params = {
            device_type: this.deviceType,
            action_group_id: prompt.action_group_id,
        };

        return http
            .get(`${this.endpoint}/paths/${prompt.id}/impression`, { headers, params })
            .catch(this.trackRedfastError);
    }

    // https://docs.redfast.com/advanced/platforms/promo-custom-render.html#dismiss-api
    sendDismiss(prompt, dismissType) {
        const headers = {
            'user-id': this.userId,
        };

        const params = {
            click: dismissType,
            device_type: this.deviceType,
            action_group_id: prompt.action_group_id,
        };

        return http
            .get(`${this.endpoint}/paths/${prompt.id}/dismiss`, { headers, params })
            .catch(this.trackRedfastError);
    }

    // https://docs.redfast.com/advanced/platforms/promo-custom-render.html#dismiss-api
    sendGoal(prompt) {
        const headers = {
            'user-id': this.userId,
        };

        const params = {
            device_type: this.deviceType,
            action_group_id: prompt.action_group_id,
        };

        return http
            .get(`${this.endpoint}/paths/${prompt.id}/goal`, { headers, params })
            .catch(this.trackRedfastError);
    }

    trackRedfastError(error) {
        trackAction('trackAppLog', {
            appLogText: error?.message,
            appLogType: 'Redfast Error',
            appLogCode: error?.response?.status,
        });
    }
}
